import type { RosterDayStatus, RosterDriverDto, RosterGroupRowsDto } from '@my/directory-api';
import { cn } from '@my/ui';
import { isWeekend, STATUS, weekdayLabel, type MonthInfo } from '../lib/mockData';

/** Compliance roll-up → the driver-name status dot colour. */
const COMPLIANCE_DOT: Record<string, string> = {
  Green: 'bg-emerald-500',
  Yellow: 'bg-amber-500',
  Red: 'bg-red-500',
};

/**
 * Monthly roster grid — drivers grouped, one column per day, a status code per
 * cell. Right-click a driver row for the add-block / add-leave menu. Driven by
 * live roster data (`useRoster`); cells aren't editable yet.
 */
export function RosterGrid({
  month,
  groups,
  isLoading,
  isError,
  onRowContextMenu,
}: {
  month: MonthInfo;
  groups: RosterGroupRowsDto[];
  isLoading?: boolean;
  isError?: boolean;
  onRowContextMenu: (resourceId: string, x: number, y: number, day?: number) => void;
}) {
  const days = Array.from({ length: month.days }, (_, i) => i + 1);
  const colSpan = 3 + month.days;

  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-md border border-slate-200 bg-white">
      <table className="border-collapse text-[11px]">
        <thead className="sticky top-0 z-20 bg-slate-700 uppercase tracking-wide text-white">
          <tr>
            <th className="sticky left-0 z-30 w-40 border-b border-r border-slate-600 bg-slate-700 px-2 py-1.5 text-left font-semibold">
              Driver
            </th>
            <th className="border-b border-slate-600 px-2 py-1.5 text-left font-semibold">Start</th>
            <th className="border-b border-r border-slate-600 px-2 py-1.5 text-left font-semibold">
              Location
            </th>
            {days.map((d) => (
              <th
                key={d}
                className={cn(
                  'w-9 border-b border-r border-slate-600 px-1 py-1 text-center font-medium',
                  isWeekend(month, d) ? 'bg-slate-800 text-slate-400' : 'text-white',
                )}
              >
                <div className="text-[9px]">{weekdayLabel(month, d)}</div>
                <div className="font-semibold">{d}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading && <MessageRow colSpan={colSpan}>Loading roster…</MessageRow>}
          {isError && !isLoading && (
            <MessageRow colSpan={colSpan}>Couldn't load the roster. Try again.</MessageRow>
          )}
          {!isLoading && !isError && groups.length === 0 && (
            <MessageRow colSpan={colSpan}>No drivers rostered for this month.</MessageRow>
          )}
          {!isLoading &&
            !isError &&
            groups.map((group) => (
              <GroupSection
                key={group.groupId ?? group.groupName}
                name={group.groupName}
                drivers={group.drivers}
                month={month}
                days={days}
                onRowContextMenu={onRowContextMenu}
              />
            ))}
        </tbody>
      </table>
    </div>
  );
}

function MessageRow({ colSpan, children }: { colSpan: number; children: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-3 py-8 text-center text-slate-400">
        {children}
      </td>
    </tr>
  );
}

function GroupSection({
  name,
  drivers,
  month,
  days,
  onRowContextMenu,
}: {
  name: string;
  drivers: RosterDriverDto[];
  month: MonthInfo;
  days: number[];
  onRowContextMenu: (resourceId: string, x: number, y: number, day?: number) => void;
}) {
  return (
    <>
      <tr>
        <td
          colSpan={3 + month.days}
          className="sticky left-0 border-y border-slate-200 bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500"
        >
          {name}
        </td>
      </tr>
      {drivers.map((d) => (
        <DriverRow
          key={d.resourceId}
          driver={d}
          month={month}
          onRowContextMenu={onRowContextMenu}
        />
      ))}
      <FooterRow label="Total" days={days} />
      <FooterRow label="Holidays" days={days} />
    </>
  );
}

function DriverRow({
  driver,
  month,
  onRowContextMenu,
}: {
  driver: RosterDriverDto;
  month: MonthInfo;
  onRowContextMenu: (resourceId: string, x: number, y: number, day?: number) => void;
}) {
  const days = Array.from({ length: month.days }, (_, i) => i);
  return (
    <tr
      onContextMenu={(e) => {
        e.preventDefault();
        onRowContextMenu(driver.resourceId, e.clientX, e.clientY);
      }}
      className="group hover:bg-slate-50"
    >
      <td className="sticky left-0 z-10 w-40 border-b border-r border-slate-100 bg-white px-2 py-1 group-hover:bg-slate-50">
        <span className="inline-flex items-center gap-1.5">
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              COMPLIANCE_DOT[driver.complianceIndicator] ?? 'bg-slate-300',
            )}
          />
          <span className="font-medium text-slate-800">{driver.name}</span>
          {driver.casual && (
            <span className="rounded bg-amber-100 px-1 text-[9px] font-medium text-amber-700">Casual</span>
          )}
        </span>
      </td>
      <td className="border-b border-slate-100 px-2 py-1 text-slate-500">{driver.start ?? '—'}</td>
      <td className="border-b border-r border-slate-100 px-2 py-1 text-slate-500">
        {driver.location ?? '—'}
      </td>
      {days.map((i) => {
        const code: RosterDayStatus = driver.days[i] ?? 'D/O';
        return (
          <td
            key={i}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRowContextMenu(driver.resourceId, e.clientX, e.clientY, i + 1);
            }}
            className={cn(
              'border-b border-r border-slate-100 px-1 py-1 text-center',
              isWeekend(month, i + 1) && code === 'D/O' && 'bg-slate-50',
              STATUS[code].cell,
            )}
          >
            {STATUS[code].label}
          </td>
        );
      })}
    </tr>
  );
}

function FooterRow({ label, days }: { label: string; days: number[] }) {
  return (
    <tr className="text-slate-400">
      <td className="sticky left-0 z-10 border-b border-r border-slate-100 bg-white px-2 py-1 text-[10px] font-medium">
        {label}
      </td>
      <td className="border-b border-slate-100" />
      <td className="border-b border-r border-slate-100" />
      {days.map((d) => (
        <td key={d} className="border-b border-r border-slate-100" />
      ))}
    </tr>
  );
}
